import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { HeartHandshake, MessagesSquare, Mail, ArrowRight, Lock } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import AanmeldModal from "./AanmeldModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const items = [
  {
    icon: HeartHandshake,
    groep: "Actieve vrijwilliger",
    text: "Steek de handen uit de mouwen. Help concreet mee — bij activiteiten, organisatie of de uitvoering van plannen.",
    cta: "Meld je aan",
    modalDescription: "Top dat je actief wilt meehelpen! Laat je gegevens achter, dan nemen we contact met je op.",
  },
  {
    icon: MessagesSquare,
    groep: "Meedenker",
    text: "Praat en denk mee over de plannen. Kom naar de dorpsvergadering en deel je ideeën en mening.",
    cta: "Denk mee",
    modalDescription: "Fijn dat je wilt meedenken! Je ontvangt uitnodigingen voor dorpsvergaderingen en momenten om mee te praten.",
  },
  {
    icon: Mail,
    groep: "Geïnformeerd blijven",
    text: "Wil je vooral op de hoogte blijven? Ontvang updates over beslismomenten en ontwikkelingen.",
    cta: "Schrijf je in",
    modalDescription: "We houden je op de hoogte van alle beslismomenten en ontwikkelingen rond de accommodaties.",
  },
];

function NewsletterForm() {
  const [email, setEmail] = useState("");
  const [voornaam, setVoornaam] = useState("");
  const [segment, setSegment] = useState("Ik wil alleen updates ontvangen");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  async function submit(e) {
    e.preventDefault();
    if (!email) {
      toast.error("Vul je e-mailadres in.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/aanmelden`, { email, voornaam, segment });
      toast.success("Bedankt voor je aanmelding! Je staat nu op de lijst.");
      setEmail("");
      setVoornaam("");
      setSegment("Ik wil alleen updates ontvangen");
      navigate("/bedankt");
    } catch (err) {
      toast.error("Er ging iets mis. Controleer je e-mailadres.");
    } finally {
      setLoading(false);
    }
  }

  const opties = [
    "Ik wil actief meehelpen",
    "Ik denk graag mee",
    "Ik wil alleen updates ontvangen",
  ];
  const inputClass =
    "w-full px-4 py-3 rounded-lg bg-[#FAF7F2]/5 border border-white/20 text-[#FAF7F2] placeholder:text-[#FAF7F2]/40 text-sm outline-none focus:border-[#74C69D] transition-colors";

  return (
    <form
      onSubmit={submit}
      id="newsletter-form"
      data-testid="newsletter-form"
      className="mt-12 rounded-3xl bg-[#142219] border border-white/10 p-7 sm:p-10 max-w-2xl"
    >
      <h3 className="font-heading text-2xl sm:text-3xl font-bold text-[#F4A261] mb-1">Bliuw op de hichte</h3>
      <p className="text-[#FAF7F2]/70 mb-7">Blijf op de hoogte van de toekomst van Menaam</p>

      <div className="flex flex-col gap-4">
        <div>
          <label className="block text-sm font-semibold text-[#FAF7F2]/85 mb-1.5">
            Voornaam <span className="font-normal text-[#FAF7F2]/45">(optioneel)</span>
          </label>
          <input
            type="text"
            value={voornaam}
            onChange={(e) => setVoornaam(e.target.value)}
            placeholder="Voornaam"
            data-testid="signup-voornaam"
            className={inputClass}
          />
        </div>
        <div>
          <label className="block text-sm font-semibold text-[#FAF7F2]/85 mb-1.5">E-mailadres *</label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="jij@voorbeeld.nl"
            data-testid="signup-email"
            className={inputClass}
          />
        </div>
      </div>

      <p className="text-sm font-semibold text-[#FAF7F2]/85 mt-6 mb-3">Hoe wil je meedoen?</p>
      <div className="flex flex-col gap-2.5">
        {opties.map((o) => (
          <label
            key={o}
            className={`flex items-center gap-3 rounded-lg border px-4 py-3 cursor-pointer transition-colors ${
              segment === o ? "border-[#74C69D] bg-[#74C69D]/10" : "border-white/15 hover:border-white/30"
            }`}
          >
            <input
              type="radio"
              name="segment"
              value={o}
              checked={segment === o}
              onChange={(e) => setSegment(e.target.value)}
              data-testid={`signup-segment-${opties.indexOf(o)}`}
              className="w-4 h-4 accent-[#74C69D]"
            />
            <span className="text-sm text-[#FAF7F2]">{o}</span>
          </label>
        ))}
      </div>

      <button
        type="submit"
        disabled={loading}
        data-testid="signup-submit"
        className="mt-7 inline-flex items-center justify-center gap-2 font-bold text-sm px-8 py-3.5 rounded-full bg-[#F4A261] text-[#1A2E22] hover:bg-[#f6b27e] transition-colors disabled:opacity-70"
      >
        {loading ? "Bezig…" : "Aanmelden"}
        {!loading && <ArrowRight size={17} />}
      </button>

      <p className="flex items-center gap-2 text-xs text-[#FAF7F2]/45 mt-5">
        <Lock size={13} /> Je gegevens worden niet gedeeld met derden.
      </p>
    </form>
  );
}

export default function Meedoen() {
  const [modal, setModal] = useState(null); // null | {type, description}

  const handleAction = (m) => {
    setModal({ groep: m.groep, description: m.modalDescription });
  };

  return (
    <section id="meedoen" data-testid="meedoen-section" className="py-24 sm:py-32 bg-[#1A2E22]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <Eyebrow dark testid="meedoen-eyebrow">Doe mee</Eyebrow>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-[#FAF7F2] mb-3">
            Jouw stem telt
          </h2>
          <p className="text-[#FAF7F2]/60 max-w-xl mb-12 leading-relaxed">
            De plannen zijn nog niet af — dat is juist het goede moment om mee te doen. Kies hoe jij betrokken wilt zijn.
          </p>
        </Reveal>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((m, i) => {
            const Icon = m.icon;
            return (
              <Reveal key={m.groep} delay={i * 0.08}>
                <div
                  data-testid={`meedoen-card-${i}`}
                  className="h-full flex flex-col rounded-2xl bg-white/5 border border-white/10 p-7 transition-transform hover:-translate-y-1"
                >
                  <div className="w-12 h-12 rounded-xl bg-[#74C69D]/15 text-[#74C69D] flex items-center justify-center mb-4">
                    <Icon size={22} />
                  </div>
                  <h3 className="font-heading text-xl font-bold text-[#74C69D] mb-2">{m.groep}</h3>
                  <p className="text-sm leading-relaxed text-[#FAF7F2]/60 m-0 flex-1">{m.text}</p>
                  <button
                    onClick={() => handleAction(m)}
                    data-testid={`meedoen-cta-${i}`}
                    className="group mt-5 inline-flex items-center gap-1.5 self-start font-bold text-sm text-[#74C69D] hover:text-[#8fd4b0] transition-colors"
                  >
                    {m.cta}
                    <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              </Reveal>
            );
          })}
        </div>

        <Reveal>
          <p className="mt-6 text-sm text-[#FAF7F2]/45 max-w-xl">
            Bij elke keuze kun je aangeven of je ook in een werkgroep (financiën, ontwerp of communicatie) wilt meedoen.
          </p>
        </Reveal>

        <Reveal>
          <NewsletterForm />
        </Reveal>
      </div>

      <AanmeldModal
        open={!!modal}
        onOpenChange={(o) => !o && setModal(null)}
        groep={modal?.groep}
        description={modal?.description}
      />
    </section>
  );
}
